import styles from '../index.module.css'

import React from 'react'

import type { NativeTag } from 'types/track'

import { Tooltip } from 'components/ds'

import { resolveIndicatorColor } from '../utils/indicator-colors'

type Props = {
  tag: NativeTag
  zIndex?: number
  showTooltip?: boolean
}

export const TagDot = ({
  tag,
  zIndex = 1,
  showTooltip = true,
}: Props): React.ReactElement | null => {
  const { color, name } = tag
  const background = resolveIndicatorColor(color)

  // Callers filter with getColoredTags, so an unresolved color is bad data.
  if (!background) return null

  // A span's implicit role is generic, whose name assistive tech ignores, so the
  // label needs role="img" to land. Without the tooltip the name is already text
  // beside the dot, and naming it there would read the tag twice.
  // Focusable so the tooltip is reachable without a pointer, even though the dot
  // itself does nothing on activation.
  const labeling = showTooltip
    ? {
        role: 'img',
        'aria-label': name,
        'data-testid': 'indicator-dot',
        tabIndex: 0,
      }
    : {
        'aria-hidden': true,
        'data-testid': 'indicator-swatch',
      }

  const dot = (
    <span
      className={styles.dot}
      {...labeling}
      // Palette hex comes from tag data, so it cannot live in the CSS module.
      style={{ background, zIndex }}
    />
  )

  if (!showTooltip) return dot

  return <Tooltip content={name}>{dot}</Tooltip>
}
